<template>
  <EInstantAnswer
    class="currency"
    data-test-id="currency"
    trees
    :feedback-link="{
      ticketSubject: 'Search: Currency Widget',
      widgetType: 'currency_rich'
    }"
  >
    <ELoading v-if="loading" class="currency__loading" />
    <template v-if="!loading" #context>
      {{ $t('web.widgets.currency.title') }}
    </template>
    <div v-if="!loading" class="currency__form">
      <component :is="inputWrapper" class="currency__input-wrapper">
        <EInput
          v-if="isDesktopUp"
          v-model.number="sourceAmount"
          name="sourceAmount"
          type="number"
          step="any"
          class="currency__amount"
          @input="calculateTarget"
        />
        <ESearchSelect
          :key="swapped ? 'origin' : 'origin-swapped'"
          v-model="sourceCurrency"
          class="currency__select"
          name="sourceCurrency"
          :type-ahead="true"
          :options="localisedOptions"
          placeholder="Select an option"
          options-shown="6"
          @select="calculateTarget"
        />
        <EInput
          v-if="!isDesktopUp"
          v-model.number="sourceAmount"
          name="sourceAmount"
          type="number"
          step="any"
          class="currency__amount"
          @input="calculateTarget"
        />
      </component>
      <div class="currency__swap">
        <EButton
          class="currency-swap__button"
          variant="solid-white"
          icon="arrow-exchange"
          data-test-id="currency-swap"
          @click="swap"
        />
        <span class="currency-swap__rate">
          {{ localisedSourceCurrency }}
          1 =
          <strong>
            {{ localisedTargetCurrency }}
            {{ localisedRate }}
          </strong>
        </span>
      </div>
      <component :is="inputWrapper" class="currency__input-wrapper">
        <EInput
          v-if="isDesktopUp"
          v-model.number="targetAmount"
          name="targetAmount"
          type="number"
          step="any"
          class="currency__amount"
          @input="calculateSource"
        />
        <ESearchSelect
          :key="swapped ? 'origin' : 'origin-swapped'"
          v-model="targetCurrency"
          class="currency__select"
          name="targetCurrency"
          :type-ahead="true"
          :options="localisedOptions"
          placeholder="Select an option"
          options-shown="6"
          @select="calculateSource"
        />
        <EInput
          v-if="!isDesktopUp"
          v-model.number="targetAmount"
          name="targetAmount"
          type="number"
          step="any"
          class="currency__amount"
          @input="calculateSource"
        />
      </component>
    </div>
    <template v-if="!loading && localisedLastUpdated" #footnote>
      <div class="currency__info">
        <span class="currency-info__rate">
          1 {{ sourceCurrency }}
          = <span data-test-id="conversion-rate">{{ localisedRate }}</span>
          {{ targetCurrency }} ·
        </span>
        {{ $t('web.widgets.currency.lastupdated') }}
        {{ localisedLastUpdated }}
      </div>
    </template>
  </EInstantAnswer>
</template>

<script>
import { mapState } from 'vuex';

import { localiseDate } from '~~/common/js/universal/datetime';
import { getCountry } from '~~/common/js/universal/market-utils';
import { trackImpression, PAGE_SERP, TYPE_RICH } from '~~/search/common/js/client/analytics/rich-content';
import EButton from '~~/common/vue/components/button';
import EInput from '~~/common/vue/components/input';
import ESearchSelect from '~~/common/vue/components/select/search-select';
import ESegmentedInput from '~~/common/vue/components/input/segmented-input';
import screen from '~~/common/vue/mixins/screen';
import { localisePlainNumber } from '~~/common/js/universal/intl';
import EInstantAnswer from '~/components/instant-answers/base';
import ELoading from '~~/common/vue/components/loading';

import { requestCurrencies, requestConvertedAmount } from './openexchangerates';
import { euroMarkets, roundToMillis } from './currency-utils';

export const TRACK_ID = 'currency';
const FALLBACK_CURRENCY = 'USD';

export default {
  name: 'ECurrency',
  components: {
    EInstantAnswer,
    EInput,
    EButton,
    ESegmentedInput,
    ESearchSelect,
    ELoading,
  },
  mixins: [screen],
  props: {
    initialSourceCurrency: {
      type: String,
      default: '',
    },
    initialTargetCurrency: {
      type: String,
      default: '',
    },
    amount: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      loading: true,
      swapped: false,
      sourceCurrency: '',
      targetCurrency: '',
      sourceAmount: this.amount,
      targetAmount: 1,
      conversionRate: 1,
      lastUpdated: null,
      currencies: [],
    };
  },
  computed: {
    ...mapState('global', ['locale']),
    ...mapState('user', ['bingMarketCode']),
    /**
     * The currency code based on the user market.
     * Used as fallback if currency detection fails.
     */
    localeCurrency() {
      if (!this.currencies) {
        return '';
      }

      // Countries which use the Euro won't be listed in the API currencies list,
      // so we have to maintain our own list.
      if (euroMarkets.includes(this.bingMarketCode)) {
        return 'EUR';
      }

      const localeCountry = getCountry(this.bingMarketCode).toUpperCase();
      const localeCurrency = this.currencies
        .filter(({ code }) => code.startsWith(localeCountry))
        .pop();

      return localeCurrency?.code || FALLBACK_CURRENCY;
    },
    localisedRate() {
      return localisePlainNumber(this.locale, this.conversionRate);
    },
    currencyNames() {
      return new Intl.DisplayNames([this.locale], { type: 'currency' });
    },
    inputWrapper() {
      return this.isDesktopUp ? 'div' : 'ESegmentedInput';
    },
    localisedSourceCurrency() {
      return this.sourceCurrency ?? this.currencyNames.of(this.sourceCurrency);
    },
    localisedTargetCurrency() {
      return this.targetCurrency ?? this.currencyNames.of(this.targetCurrency);
    },
    /**
     * Options to populate the 2 currency selects.
     * Each option has {value: string, label: string}.
     */
    localisedOptions() {
      return this.currencies.map(({ code }) => ({
        label: this.isDesktopUp ?
          [code, '-', this.currencyNames.of(code)].join(' ') :
          [code, this.currencyNames.of(code)].join(' '),
        value: code,
      }));
    },
    localisedLastUpdated() {
      const lastUpdatedDate = new Date(this.lastUpdated);
      return `${
        localiseDate(this.locale, lastUpdatedDate, { month: 'short' })
      } ${
        lastUpdatedDate.toLocaleTimeString(this.locale, { timeZoneName: 'short' })
      }`;
    },
  },
  mounted() {
    const isEmpty = (!this.initialSourceCurrency && !this.initialTargetCurrency) || !this.amount;
    const trackingOptions = {
      name: TRACK_ID,
      page: PAGE_SERP,
      type: TYPE_RICH,
      once: false,
      property: isEmpty ? 'empty' : '',
    };

    trackImpression(trackingOptions);

    requestCurrencies()
      .then(async (currencies) => {
        this.currencies = Object.keys(currencies).map((code) => ({
          code,
          name: currencies[code],
        }));
        this.sourceCurrency = this.getCurrencyWithFallback(
          this.initialSourceCurrency,
          this.initialTargetCurrency,
        );
        this.targetCurrency = this.getCurrencyWithFallback(
          this.initialTargetCurrency,
          this.sourceCurrency,
        );
        await this.getRate();
        this.loading = false;
      });
  },
  methods: {
    async getRate() {
      const {
        amount,
        conversionRate,
        timestamp,
      } = await requestConvertedAmount(
        this.sourceAmount,
        this.sourceCurrency,
        this.targetCurrency,
      );
      this.lastUpdated = new Date(timestamp);
      this.conversionRate = conversionRate;
      this.targetAmount = roundToMillis(amount);
    },
    async calculateTarget() {
      const amount = this.sourceAmount * this.conversionRate;
      this.targetAmount = roundToMillis(amount);
    },
    async calculateSource() {
      const amount = this.targetAmount / this.conversionRate;
      this.sourceAmount = roundToMillis(amount);
    },
    /**
     * Swaps position of the currencies and values.
     * Flips the value of this.swapped to force-update the selects.
     */
    swap() {
      [this.sourceCurrency, this.targetCurrency] =
      [this.targetCurrency, this.sourceCurrency];
      [this.sourceAmount, this.targetAmount] =
      [this.targetAmount, this.sourceAmount];
      this.swapped = !this.swapped;
      this.conversionRate = 1 / this.conversionRate;
    },
    getCurrencyWithFallback(primary, secondary) {
      if (primary) {
        return primary.toUpperCase();
      }
      if (secondary !== this.localeCurrency) {
        return this.localeCurrency;
      }

      return FALLBACK_CURRENCY;
    },
  },
};
</script>

<style lang="scss">
// Approximate height of the form
$min-height: 150px;

.currency__loading {
  display: flex;
  min-height: $min-height;
}

.currency__form {
  margin-bottom: $space-s;
}

.currency-swap__button {
  vertical-align: middle;
}

.currency-swap__rate {
  margin-left: $space-1s;
}

@include desktop {
  .currency__input-wrapper {
    flex-grow: 1;
  }

  .currency__form {
    display: flex;
    align-items: flex-end;
  }

  .currency__amount {
    margin-bottom: $space-1s;
  }

  .currency__swap {
    padding: 0 $space-1s;
  }

  .currency-swap__rate {
    display: none;
  }
}

@include desktop-down {
  .currency-swap__button {
    transform: rotate(90deg);
  }

  .currency-info__rate {
    display: none;
  }

  .currency__swap {
    margin: $space-1s 0;
  }

  .currency__select {
    order: 0;
    max-width: 130px;
  }

  .currency__amount .input__element {
    text-align: right;

    // Because of spacing issues.
    // This shouldn't affect display in actual mobile devices.
    -moz-appearance: textfield;
    appearance: textfield;
  }
}
</style>
